import moment from 'moment';

import {
  type DeltaInterval,
  DeltaIntervalType,
  ProceduralJurisdiction,
} from '../../../../../../../../backend/src/matter/procedural-timeline/procedural-timeline-enums';
import type {
  ProceduralTimelineEventSuggestion,
  SuggestionUpdatedField,
} from '../../../../../../../../backend/src/matter/procedural-timeline/procedural-timeline-event-suggestion.entity';
import type { ExtractionState } from '@/api/hooks/useProceduralStream';
import { CountingKey } from '@/new/stores/timeline-calculator-store';
import { getFrontendURLRegion } from '@/utils/checkFrontendURL';

/**
 * The per-phase progress detail (counts, save breakdown, completion message)
 * for an in-flight extraction. Shared by the full-page loader and the inline
 * progress banner so the two stay in lockstep. The phase *labels* live with
 * each component because they're phrased differently (sentences vs. chips).
 */
export function getExtractionDetail(state: ExtractionState): string | null {
  switch (state.phase) {
    case 'reading':
      return state.chunkCount !== null ? `${state.chunkCount} sections found` : null;
    case 'extracting':
      return state.eventCount !== null
        ? `${state.eventCount} deadline${state.eventCount === 1 ? '' : 's'} currently extracted`
        : null;
    case 'merging':
      return state.decisionCount !== null
        ? `${state.decisionCount} comparison${state.decisionCount === 1 ? '' : 's'} so far`
        : null;
    case 'done':
      return 'Review the suggestions to add them to the timeline.';
    case 'failed':
      return state.error;
  }
}

/**
 * Whether an Update suggestion flags `field` in its `updatedFields` list.
 * The list is the authoritative record of what the update changes: a listed
 * field's `proposed*` value applies even when null (an intentional clear), so
 * consumers must check this list rather than sniffing proposed values for
 * null. Null lists (Add and Delete suggestions) flag nothing.
 */
function hasUpdatedField(suggestion: ProceduralTimelineEventSuggestion, field: `${SuggestionUpdatedField}`): boolean {
  return (suggestion.updatedFields ?? []).includes(field as SuggestionUpdatedField);
}

/**
 * Briefly ring-highlight an event card to draw the eye to it. Retries while
 * the row mounts (e.g. right after accepting a suggestion, the event row only
 * appears once the events query refetch lands). No-ops if the row never
 * mounts, such as after accepting a delete suggestion.
 */
export function flashEventRow(eventId: string, attempts = 10): void {
  const el = document.getElementById(`procedural-event-${eventId}`);
  if (el === null) {
    if (attempts > 0) window.setTimeout(() => flashEventRow(eventId, attempts - 1), 150);
    return;
  }
  el.classList.add('ring-2', 'ring-teal-400');
  window.setTimeout(() => el.classList.remove('ring-2', 'ring-teal-400'), 1000);
}

// Both formatters read the stored timestamp's UTC components rather than
// converting to the viewer's timezone. Procedural dates are civil dates stored
// at UTC midnight (midnight doubles as the "no time set" sentinel), so
// browser-local rendering would shift the date for west-of-UTC viewers and
// invent phantom times (such as "at 1:00 AM" during BST) on date-only events.
export function formatDate(date: Date | string | null | undefined): string {
  if (!date) return 'Date TBD';
  return moment.utc(date).format('MMM DD, YYYY');
}

export function formatDateRange(start: Date | string | null, end: Date | string | null): string | null {
  if (!start) return null;
  const s = moment.utc(start);
  const startHasTime = s.hour() !== 0 || s.minute() !== 0;
  const startStr = s.format(startHasTime ? 'MMM DD, YYYY [at] h:mm A' : 'MMM DD, YYYY');
  if (!end) return startStr;
  const e = moment.utc(end);
  const endHasTime = e.hour() !== 0 || e.minute() !== 0;
  const sameDay = s.isSame(e, 'day');
  if (sameDay) {
    if (!startHasTime && !endHasTime) return startStr;
    return `${startStr} – ${e.format('h:mm A')}`;
  }
  const endStr = e.format(endHasTime ? 'MMM DD, YYYY [at] h:mm A' : 'MMM DD, YYYY');
  return `${startStr} – ${endStr}`;
}

/**
 * Reverse of {@link mapDeltaTypeToCounting}: resolve a calculator counting
 * selection back to the `(deltaIntervalType, jurisdiction)` pair we persist on
 * the event. Keyed by `CountingKey` so adding a new calculator option forces a
 * matching entry here at compile time.
 */
const COUNTING_TO_EVENT: Record<
  CountingKey,
  { deltaIntervalType: DeltaIntervalType; jurisdiction: ProceduralJurisdiction }
> = {
  'uk-ew-calendar': {
    deltaIntervalType: DeltaIntervalType.Calendar,
    jurisdiction: ProceduralJurisdiction.EnglandAndWales,
  },
  'uk-ew-clear-cpr': {
    deltaIntervalType: DeltaIntervalType.Clear,
    jurisdiction: ProceduralJurisdiction.EnglandAndWales,
  },
  'uk-ew-business': {
    deltaIntervalType: DeltaIntervalType.Working,
    jurisdiction: ProceduralJurisdiction.EnglandAndWales,
  },
  'us-federal-calendar': {
    deltaIntervalType: DeltaIntervalType.Calendar,
    jurisdiction: ProceduralJurisdiction.USFederal,
  },
  'us-federal-business': {
    deltaIntervalType: DeltaIntervalType.Working,
    jurisdiction: ProceduralJurisdiction.USFederal,
  },
};

export function mapCountingToEvent(key: CountingKey): {
  deltaIntervalType: DeltaIntervalType;
  jurisdiction: ProceduralJurisdiction;
} {
  return COUNTING_TO_EVENT[key];
}

/**
 * Region-derived default jurisdiction for new procedural work. The deployment
 * region (from the frontend URL) is the best available signal for which court
 * rules a matter follows. Australia has no dedicated rule set yet, so it falls
 * back to "Other"; everything else defaults to England & Wales.
 */
export function defaultJurisdiction(): ProceduralJurisdiction {
  switch (getFrontendURLRegion()) {
    case 'us':
      return ProceduralJurisdiction.USFederal;
    case 'au':
      return ProceduralJurisdiction.Other;
    case 'uk':
    case 'staging':
    default:
      return ProceduralJurisdiction.EnglandAndWales;
  }
}

/**
 * Region-derived default counting method for the linked-date picker / calculator,
 * kept in lockstep with {@link defaultJurisdiction} so a US deployment defaults to
 * US Federal counting rather than UK E&W. Jurisdictions without their own counting
 * rules (Other) reuse the UK E&W calendar fallback baked into mapDeltaTypeToCounting.
 */
export function defaultCountingKey(): CountingKey {
  return mapDeltaTypeToCounting(DeltaIntervalType.Calendar, defaultJurisdiction());
}

/**
 * Seed the calculator's counting selector from the event's day-count type +
 * jurisdiction. Unsupported jurisdictions (Other / null) fall back to the
 * UK E&W calendar default so the calculator still opens to something usable.
 */
export function mapDeltaTypeToCounting(
  type: DeltaIntervalType,
  jurisdiction: ProceduralJurisdiction | null,
): CountingKey {
  if (jurisdiction === ProceduralJurisdiction.USFederal) {
    switch (type) {
      case DeltaIntervalType.Working:
      case DeltaIntervalType.Court:
        return 'us-federal-business';
      case DeltaIntervalType.Calendar:
      case DeltaIntervalType.Clear:
      default:
        return 'us-federal-calendar';
    }
  }
  switch (type) {
    case DeltaIntervalType.Clear:
      return 'uk-ew-clear-cpr';
    case DeltaIntervalType.Working:
    case DeltaIntervalType.Court:
      return 'uk-ew-business';
    case DeltaIntervalType.Calendar:
    default:
      return 'uk-ew-calendar';
  }
}

interface EffectiveView {
  name: string;
  description: string | null;
  startDateTime: Date | string | null;
  endDateTime: Date | string | null;
  timeZone: string | null;
}

/**
 * The post-accept view of a suggestion: an Add reads its proposed values, an
 * Update overlays exactly its `updatedFields` on the existing event (a listed
 * field's proposed value applies even when null — an intentional clear). Name,
 * description, and time zone are never changed by an update.
 */
export function effectiveView(suggestion: ProceduralTimelineEventSuggestion): EffectiveView {
  const existing = suggestion.existingEvent;
  if (suggestion.kind === 'add') {
    return {
      name: suggestion.proposedName ?? '(unnamed)',
      description: suggestion.proposedDescription,
      startDateTime: suggestion.proposedStartDateTime,
      endDateTime: suggestion.proposedEndDateTime,
      timeZone: suggestion.proposedTimeZone,
    };
  }
  return {
    name: existing?.name ?? '(unknown event)',
    description: existing?.description ?? null,
    startDateTime: hasUpdatedField(suggestion, 'startDateTime')
      ? suggestion.proposedStartDateTime
      : (existing?.startDateTime ?? null),
    endDateTime: hasUpdatedField(suggestion, 'endDateTime')
      ? suggestion.proposedEndDateTime
      : (existing?.endDateTime ?? null),
    timeZone: existing?.timeZone ?? null,
  };
}

/** Mirror the post-accept confirmation state for the Unconfirmed chip. */
export function isEffectivelyUnconfirmed(s: ProceduralTimelineEventSuggestion): boolean {
  if (s.kind === 'add') return s.proposedIsConfirmed !== true;
  return hasUpdatedField(s, 'isConfirmed') ? s.proposedIsConfirmed !== true : s.existingEvent?.isConfirmed === false;
}

interface EffectiveAnchor {
  anchorEventId: string | null;
  interval: DeltaInterval | null;
  intervalType: ProceduralTimelineEventSuggestion['proposedDeltaIntervalType'];
}

/**
 * Post-accept anchor relationship, overlaid like the dates: an Update applies a
 * field only when `updatedFields` lists it, otherwise the existing event's
 * value shows through. Without this, a partial anchor change (only the target,
 * or only the interval) drops its unchanged half, and an update that leaves the
 * anchor alone loses the relationship entirely.
 */
export function effectiveAnchor(s: ProceduralTimelineEventSuggestion): EffectiveAnchor {
  const existing = s.existingEvent;
  if (s.kind === 'add') {
    return {
      anchorEventId: s.proposedAnchorExistingEventId,
      interval: s.proposedDeltaInterval,
      intervalType: s.proposedDeltaIntervalType,
    };
  }
  return {
    anchorEventId: hasUpdatedField(s, 'anchorEventId')
      ? s.proposedAnchorExistingEventId
      : (existing?.anchorEventId ?? null),
    interval: hasUpdatedField(s, 'deltaInterval') ? s.proposedDeltaInterval : (existing?.deltaInterval ?? null),
    intervalType: hasUpdatedField(s, 'deltaIntervalType')
      ? s.proposedDeltaIntervalType
      : (existing?.deltaIntervalType ?? null),
  };
}
